import {
  type BaseQueryFn,
  type skipToken,
  type TypedUseInfiniteQuery,
} from '@reduxjs/toolkit/query/react';
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';

import {
  type UsePaginatedInfiniteQueryHookResults,
  type UsePaginatedInfiniteQueryHookSubscriptionOptions,
  type UsePaginatedInfiniteQueryHookWithSelectFromPageResults,
} from './create-paginated-infinite-query-hook.types';

export const createPaginatedInfiniteQueryHook = <
  ResultType,
  QueryArg extends { query: { limit?: number } } | typeof skipToken,
  PageParam,
  BaseQuery extends BaseQueryFn,
  UseInfiniteQuery extends TypedUseInfiniteQuery<
    ResultType,
    QueryArg,
    PageParam,
    BaseQuery
  > = TypedUseInfiniteQuery<ResultType, QueryArg, PageParam, BaseQuery>,
>(
  useInfiniteQuery: TypedUseInfiniteQuery<
    ResultType,
    QueryArg,
    PageParam,
    BaseQuery
  >,
) => {
  // hook-scoped type aliases
  type UseInfiniteQueryParameters = Parameters<UseInfiniteQuery>;
  type UsePaginatedQueryArg = UseInfiniteQueryParameters[0];
  // We need to add the missing subscription option: refetchOnMountOrArgChange, which is not included in TypedUseInfiniteQueryStateOptions
  type UsePaginatedQuerySubscriptionOptions =
    UsePaginatedInfiniteQueryHookSubscriptionOptions<
      ResultType,
      UsePaginatedQueryArg,
      PageParam,
      BaseQuery
    >;

  /**
   * An infinite query hook wrapped with APIs refined for tabular pagination. This includes
   * methods to control client-side pagination across infinite query pages, a collection of
   * derived pagination states, and access to the `currentPage` that has been optimised
   * for render performance through `selectFromResult`.
   *
   * ## `selectFromPage` API
   *
   * In addition, the hook exposes a `selectFromPage` optional parameter that substitutes for
   * the generic `selectFromResult` API. Given that this hook is only ever concerned with the
   * state of the currently viewed page, `selectFromPage` allows components to further refine
   * subscriptions to specific entries within the `currentPage`.
   *
   * @param queryArg The `QueryArg` expected by the underlying `useInfiniteQuery` hook.
   * @param subscriptionOptions `useInfiniteQuery` subscription options, omitting the `selectFromResult` hook.
   * @param selectFromPage A callback substituting for `selectFromResult` to allow consumers to subscribe to specific state slices within the `currentPage`.
   */
  function usePaginatedInfiniteQuery(
    queryArg: UsePaginatedQueryArg,
    subscriptionOptions: UsePaginatedQuerySubscriptionOptions,
  ): UsePaginatedInfiniteQueryHookResults<ResultType>;

  function usePaginatedInfiniteQuery<
    SelectFromPageResult extends Record<string, unknown>,
  >(
    queryArg: UsePaginatedQueryArg,
    subscriptionOptions: UsePaginatedQuerySubscriptionOptions,
    /**
     * Select any data from the current page result. This is analogous to the `selectFromResult`
     * option but automatically applies your callback to the currently viewed page.
     *
     * Always return an object from this function. This is because RTK Query performs a shallow
     * comparison of the object returned from `selectFromResult` to determine whether a hook needs
     * to re-render. If defined, the result of `selectFromPage` will be merged into the top-level
     * object returned by this hook, substituting for `currentPage`.
     *
     * https://redux-toolkit.js.org/rtk-query/usage/queries#selecting-data-from-a-query-result
     */
    selectFromPage?: (page: ResultType | undefined) => SelectFromPageResult,
  ): UsePaginatedInfiniteQueryHookWithSelectFromPageResults<SelectFromPageResult>;

  function usePaginatedInfiniteQuery<
    SelectFromPageResult extends Record<string, unknown>,
  >(
    queryArg: UsePaginatedQueryArg,
    subscriptionOptions: UsePaginatedQuerySubscriptionOptions,
    selectFromPage?: (page: ResultType | undefined) => SelectFromPageResult,
  ) {
    const { initialPageIndex, initialPageSize, ...restSubscriptionOptions } =
      subscriptionOptions ?? {};
    const [pageIndex, setPageIndex] = useState<number>(initialPageIndex);
    const [pageSize, setPageSize] = useState<number>(initialPageSize);

    const memoizedQueryArg = useMemo(() => {
      if (typeof queryArg === 'object') {
        return {
          ...queryArg,
          query: { ...(queryArg.query ?? {}), limit: pageSize },
        };
      }
      return queryArg;
    }, [queryArg, pageSize]);

    const result = useInfiniteQuery(memoizedQueryArg, {
      ...restSubscriptionOptions,
      selectFromResult: ({
        data,
        isFetching,
        isLoading,
        isUninitialized,
        isError,
        error,
        isFetchingNextPage,
        hasNextPage,
      }) => {
        const currentPage = data?.pages[pageIndex];
        const pageCount = data?.pages.length ?? 0;

        const defaultResult = {
          pageCount,
          isFetching,
          isLoading,
          isUninitialized,
          isError,
          error,
          isFetchingNextPage,
          hasNextPage,
        };

        if (selectFromPage) {
          return {
            ...defaultResult,
            ...selectFromPage(currentPage),
          };
        }

        return {
          ...defaultResult,
          currentPage,
        };
      },
    });
    const {
      fetchNextPage,
      pageCount,
      isFetchingNextPage,
      isFetching,
      isUninitialized,
      isLoading,
    } = result;

    // render booleans
    const isFetchingCurrPage = pageIndex >= pageCount && isFetching;
    const nextPageAlreadyFetched = pageIndex + 1 <= pageCount - 1;
    const canGetNextPage =
      ((pageIndex + 1 <= pageCount - 1 || result.hasNextPage) &&
        !isFetchingNextPage) ||
      nextPageAlreadyFetched;
    const canGetPrevPage = pageIndex > 0;

    /** Go forwards by 1 page. */
    const getNextPage = useCallback(async () => {
      if (nextPageAlreadyFetched) {
        setPageIndex((prevPageIndex) => prevPageIndex + 1);
        return;
      } else if (canGetNextPage) {
        setPageIndex((prevPageIndex) => prevPageIndex + 1);
        await fetchNextPage();
      }
    }, [nextPageAlreadyFetched, canGetNextPage, fetchNextPage]);

    /** Go back by 1 page. */
    const getPrevPage = useCallback(() => {
      if (pageIndex <= 0) return;
      setPageIndex((prevPageIndex) => prevPageIndex - 1);
    }, [pageIndex]);

    /**
     * Check if the current cache entry already has a specific page.
     */
    const getHasPage = useCallback(
      (pageIndex: number) => {
        return pageIndex >= 0 && pageIndex < pageCount;
      },
      [pageCount],
    );

    /**
     * Jump to a visited page by index. Otherwise, will throw an error.
     */
    const jumpToVisitedPage = useCallback(
      (pageIndex: number) => {
        if (!getHasPage(pageIndex))
          throw new Error(`Page index ${pageIndex} has not been visited yet`);
        setPageIndex(pageIndex);
      },
      [getHasPage],
    );

    /**
     * Always reset pagination to the first page if `queryArg` changes.
     * except on mount (as on mount it is not necessary to do it
     * and a page index other than 0 can be provided)
     */
    const isFirstRenderRef = useRef(true);
    useLayoutEffect(() => {
      if (isFirstRenderRef.current) {
        isFirstRenderRef.current = false;
        return;
      }
      setPageIndex(0);
    }, [queryArg]);

    // When initialPageIndex is provided
    // but the page under that index is not in the cache
    // we need to reset the page index to 0
    useLayoutEffect(() => {
      if (
        !isUninitialized &&
        !isLoading &&
        !isFetching &&
        !isFetchingNextPage &&
        !getHasPage(pageIndex)
      ) {
        setPageIndex(0);
      }
    }, [
      getHasPage,
      pageIndex,
      isFetching,
      isFetchingNextPage,
      isLoading,
      isUninitialized,
    ]);

    const changePageSize = useCallback((pageSize: number) => {
      setPageIndex(0);
      setPageSize(pageSize);
    }, []);

    return useMemo(() => {
      const {
        pageCount,
        isFetching,
        isLoading,
        isUninitialized,
        isError,
        error,
        isFetchingNextPage,
        hasNextPage,
        ...selectFromPageResults
      } = result;

      return {
        getNextPage,
        getPrevPage,
        setPageSize: changePageSize,
        getHasPage,
        jumpToVisitedPage,
        paginationState: {
          pageCount,
          pageIndex,
          pageSize,
          canGetNextPage: canGetNextPage && !isLoading,
          canGetPrevPage,
          isFetchingCurrPage,
          isFetching,
        },
        infiniteQueryResults: {
          isFetching,
          isLoading,
          isUninitialized,
          isError,
          error,
          isFetchingNextPage,
          hasNextPage,
        },
        ...(selectFromPageResults as
          | SelectFromPageResult
          | { currentPage: ResultType | undefined }),
      };
    }, [
      getHasPage,
      getNextPage,
      getPrevPage,
      changePageSize,
      jumpToVisitedPage,
      canGetNextPage,
      canGetPrevPage,
      isFetchingCurrPage,
      pageIndex,
      pageSize,
      result,
    ]);
  }

  return usePaginatedInfiniteQuery;
};
